import { useDictationHotkey } from '@/composition/hosts/dictation/hooks/useDictationHotkey';
import { useIsDictationEnabled } from '@/composition/hosts/dictation/hooks/useIsDictationEnabled';
import {
  getDictationMode,
  useDictationStore,
} from '@/features/dictation/dictationBubble/store/useDictationStore';
import {
  isInStoppedState,
  isSessionRecordingActive,
} from '@/features/dictation/dictationBubble/ui/hooks/dictationHotkeyHelpers';
import { isTauri } from '@heidi/runtime/platform';
import { useCallback, useEffect, useRef } from 'react';

const DEFAULT_DOUBLE_TAP_INTERVAL_MS = 400;

/**
 * Map the web chord's single token to the `KeyboardEvent.code` we match. Web
 * dictation chords have no modifiers, so we read the first (and only) token.
 * Main's default stores it as the backtick character (`['`']`) so we map
 * that back to its canonical name. Everything else (Backquote, IntlYen, …)
 * is already a valid `e.code`.
 */
const chordToEventCode = (chord: readonly string[]): string => {
  const token = chord.at(0);
  if (!token || token === '`') return 'Backquote';
  return token;
};

/**
 * Detects dictation hotkey patterns using the user's configured trigger key
 * (default Backquote; IntlYen on JIS keyboards):
 * - Hold: Press and hold → release to stop
 * - Hands-free: Double-tap → tap again to stop
 */
export const useWebDictationHotkey = (): void => {
  const stateRef = useRef({
    isRecording: false,
    awaitingSecondTap: false,
    isEndingHandsFree: false,
    doubleTapTimer: undefined as ReturnType<typeof setTimeout> | undefined,
    keyDownTime: 0,
    triggerPressed: false,
  });

  const { hotkey } = useDictationHotkey();
  const triggerCode = chordToEventCode(hotkey);

  const setMode = useDictationStore((s) => s.setDictationMode);
  const dictationStart = useDictationStore((s) => s.start);
  const dictationStop = useDictationStore((s) => s.stop);
  const isDesktopDictationEnabled = useIsDictationEnabled();

  const stopAndReset = useCallback(() => {
    const s = stateRef.current;

    clearTimeout(s.doubleTapTimer);

    s.isRecording = false;
    s.awaitingSecondTap = false;
    s.isEndingHandsFree = false;
    s.doubleTapTimer = undefined;
    s.keyDownTime = 0;

    dictationStop().catch(() => {});
    setMode(null);
  }, [setMode, dictationStop]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.code !== triggerCode || e.repeat) return;
      if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

      e.preventDefault();
      e.stopPropagation();

      const s = stateRef.current;
      s.triggerPressed = true;

      // Sync ref state with store - handle external cancellation (e.g., ESC key)
      const isStopped = isInStoppedState();
      if (s.isRecording && isStopped) {
        clearTimeout(s.doubleTapTimer);
        s.isRecording = false;
        s.awaitingSecondTap = false;
        s.isEndingHandsFree = false;
        s.doubleTapTimer = undefined;
        s.keyDownTime = 0;
      }

      // Second tap → confirm hands-free
      if (s.awaitingSecondTap) {
        clearTimeout(s.doubleTapTimer);
        s.awaitingSecondTap = false;
        setMode('HANDSFREE');
        return;
      }

      // Already in hands-free → prepare to stop
      if (getDictationMode() === 'HANDSFREE') {
        s.isEndingHandsFree = true;
        return;
      }

      // Start dictation in hold mode (only if session recording isn't active)
      if (!s.isRecording && !isSessionRecordingActive()) {
        if (!isStopped) return;

        s.isRecording = true;
        s.keyDownTime = Date.now();
        setMode('HOLD');
        dictationStart().catch(() => {});
      }
    },
    [setMode, dictationStart, triggerCode]
  );

  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.code !== triggerCode) return;

      const s = stateRef.current;
      s.triggerPressed = false;

      if (!s.isRecording) return;

      e.preventDefault();
      e.stopPropagation();

      // Ending hands-free
      if (s.isEndingHandsFree) {
        stopAndReset();
        return;
      }

      // Release in hold mode
      if (getDictationMode() === 'HOLD' && !s.awaitingSecondTap) {
        const holdDuration = Date.now() - s.keyDownTime;
        const isQuickTap = holdDuration < DEFAULT_DOUBLE_TAP_INTERVAL_MS;

        // Only allow double-tap to hands-free if this was a quick tap
        if (isQuickTap) {
          s.awaitingSecondTap = true;

          s.doubleTapTimer = setTimeout(() => {
            if (s.awaitingSecondTap) {
              stopAndReset();
            }
          }, DEFAULT_DOUBLE_TAP_INTERVAL_MS);
        } else {
          // Long hold - just stop, no hands-free transition
          stopAndReset();
        }
      }
    },
    [stopAndReset, triggerCode]
  );

  const handleFocusLost = useCallback(() => {
    const s = stateRef.current;
    s.triggerPressed = false;

    if (!s.isRecording) return;

    // Hands-free continues when focus is lost
    if (getDictationMode() === 'HANDSFREE') {
      s.isEndingHandsFree = false;
      return;
    }

    // Stop dictation if in hold mode
    if (getDictationMode() === 'HOLD') {
      stopAndReset();
    }
  }, [stopAndReset]);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'hidden') {
      handleFocusLost();
    }
  }, [handleFocusLost]);

  // Prevent character insertion into text fields when the trigger key is pressed
  const beforeInputHandler = useCallback((e: InputEvent) => {
    if (stateRef.current.triggerPressed) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, []);

  // Use capture phase to intercept events before they reach the editor
  useEffect(() => {
    // Skip on desktop when FF is enabled (useDesktopDictationHotkey handles Fn key)
    if (isTauri() && isDesktopDictationEnabled) return;

    const ref = stateRef.current;

    globalThis.addEventListener('keydown', handleKeyDown, true);
    globalThis.addEventListener('keyup', handleKeyUp, true);
    globalThis.addEventListener('blur', handleFocusLost);
    globalThis.addEventListener('beforeinput', beforeInputHandler as EventListener, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown, true);
      globalThis.removeEventListener('keyup', handleKeyUp, true);
      globalThis.removeEventListener('blur', handleFocusLost);
      globalThis.removeEventListener(
        'beforeinput',
        beforeInputHandler as EventListener,
        true
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      clearTimeout(ref.doubleTapTimer);
    };
  }, [
    handleKeyDown,
    handleKeyUp,
    handleFocusLost,
    handleVisibilityChange,
    beforeInputHandler,
    isDesktopDictationEnabled,
  ]);
};
